import { EventCategoryEnum, EventEnum, EventModEnum } from 'api/events/events.enum';
import { EventCategoryDescriptionEnum } from 'app/events/inneEvents/InneEvents.enum';
import { useLocale } from 'hooks/useLocale/useLocale';
import { AppMessages } from 'i18n/messages';
import { Server } from 'api/servers/servers.types';
import { PermissionDesctiptionEnum, PermissionEnum } from 'api/permissions/permissions.enum';
import { EventModDescriptionEnum } from 'app/events/Events.enum';
import { EVENT_MODE_NUMBERS } from 'app/events/Events.variables';
import {
  EventDescriptionEnum,
  RcpDescriptionEnum,
  RcpEnum,
  YesNoFiltersEnum,
} from 'app/events/eventsFilters/EventsFilters.enum';
import { UserStatusDescriptionEnum, UserStatusEnum } from 'api/users/users.enum';
import { CardTypeEnum } from 'api/cards/cards.enum';
import { CardTypeDescriptionEnum } from 'app/cards/Cards.enum';
import { CardBlockedDescriptionEnum, CardBlockedEnum } from 'reusable/addCard/AddCard.enum';
import { EventExportFormat, EventsExportFormatConfig } from 'api/eventsExportFormat/eventsExportFormats.types';
import { GlcValueEnum } from 'app/groups/addGroup/AddGroup.enum';
import { GroupTypeDescriptionEnum } from 'app/groups/Groups.enum';
import { GroupTypeEnum } from 'api/groups/groups.enum';
import { AnnouncementModDescriptionEnum, AnnouncementModEnum } from 'api/announcements/announcements.enum';
import {
  ScheduleHistoryTypeFilterDescriptionEnum,
  ScheduleHistoryTypeFilterEnum,
  ScheduleTransition,
  ScheduleTransitionDescriptionEnum,
  ScheduleTypeDescriptionEnum,
  ScheduleTypeEnum,
} from 'api/schedules/schedules.enum';
import { SequenceTypeDescriptionEnum, SequenceTypeEnum } from 'api/sequences/sequences.enum';
import { usePermissions } from 'hooks/usePermissions/usePermissions';
import { EventsReportInputStateEnum, EventsReportInputStateMessageEnum } from 'api/reports/reports.enum';

export const useAppSelectOptions = (args?: {
  servers?: Server[];
  eventsExportFormatConfig?: EventsExportFormatConfig;
  eventsExportFormats?: EventExportFormat[];
}) => {
  const { formatMessage } = useLocale();
  const { accessFlags } = usePermissions();

  const permissionOptions = Object.values(PermissionEnum).map((option) => ({
    value: option,
    label: formatMessage({ id: PermissionDesctiptionEnum[option] }),
  }));

  const modOptions = Object.values(EventModEnum).map((mod) => ({
    value: mod,
    label: formatMessage({ id: EventModDescriptionEnum[mod] }),
  }));

  const modeOptions = EVENT_MODE_NUMBERS.map((mode) => ({
    value: mode,
    label: String(mode),
  }));

  const rcpOptions = Object.values(RcpEnum).map((option) => ({
    value: option,
    label: formatMessage({ id: RcpDescriptionEnum[option] }),
  }));

  const eventOptions = Object.values(EventEnum).map((action) => ({
    value: action,
    label: formatMessage({ id: EventDescriptionEnum[action] }),
  }));

  const categoryOptions = Object.values(EventCategoryEnum).map((category) => ({
    value: category,
    label: formatMessage({ id: EventCategoryDescriptionEnum[category] }),
  }));

  const yesNoOptions = [
    {
      value: YesNoFiltersEnum.YES,
      label: formatMessage({ id: AppMessages['common.yes'] }),
    },
    {
      value: YesNoFiltersEnum.NO,
      label: formatMessage({ id: AppMessages['common.no'] }),
    },
  ];

  const userStatusOptions = Object.values(UserStatusEnum).map((status) => ({
    value: status,
    label: formatMessage({ id: UserStatusDescriptionEnum[status] }),
  }));

  const cardTypeOptions = (
    accessFlags.accessAllData || accessFlags.accessFeaturedCards
      ? Object.values(CardTypeEnum)
      : [CardTypeEnum.ZWYKLA, CardTypeEnum.ZASTEPCZA]
  ).map((type) => ({
    value: type,
    label: formatMessage({ id: CardTypeDescriptionEnum[type] }),
  }));

  const cardBlockedOptions = Object.values(CardBlockedEnum).map((blocked) => ({
    value: blocked,
    label: formatMessage({ id: CardBlockedDescriptionEnum[blocked] }),
  }));

  const groupTypeOptions = Object.values(GroupTypeEnum).map((type) => ({
    value: type,
    label: formatMessage({ id: GroupTypeDescriptionEnum[type] }),
  }));

  const glcOptions = [
    {
      value: GlcValueEnum.YES,
      label: formatMessage({ id: AppMessages['common.yes'] }),
    },
    {
      value: GlcValueEnum.NO,
      label: formatMessage({ id: AppMessages['common.no'] }),
    },
  ];

  const serverOptions =
    args && args.servers
      ? args.servers.map((server) => ({
          value: server.id,
          label: server.nazwa,
        }))
      : [];

  const separatorOptions =
    args && args.eventsExportFormatConfig
      ? args.eventsExportFormatConfig?.separator.map((value) => ({
          label: value,
          value,
        }))
      : [];

  const endlineOptions =
    args && args.eventsExportFormatConfig
      ? args.eventsExportFormatConfig?.endline.map((value) => ({
          label: JSON.stringify(value).replace('"', '').replace('"', ''),
          value,
        }))
      : [];

  const eventsExportFormatsOptions =
    args && args.eventsExportFormats
      ? args.eventsExportFormats.map((eventExportFormat) => ({
          value: eventExportFormat.id,
          label: eventExportFormat.nazwa,
        }))
      : [];

  const announcementModOptions = Object.values(AnnouncementModEnum).map((mod) => ({
    label: formatMessage({ id: AnnouncementModDescriptionEnum[mod] }).toUpperCase(),
    value: mod,
  }));

  const scheduleModesOptions = Object.values(ScheduleTypeEnum).map((mod) => ({
    label: formatMessage({ id: ScheduleTypeDescriptionEnum[mod] }),
    value: mod,
  }));

  const scheduleTransitionOptions = Object.values(ScheduleTransition).map((mod) => ({
    label: formatMessage({ id: ScheduleTransitionDescriptionEnum[mod] }),
    value: mod,
  }));

  const scheduleHistoryTypeFilterOptions = Object.values(ScheduleHistoryTypeFilterEnum).map((type) => ({
    label: formatMessage({ id: ScheduleHistoryTypeFilterDescriptionEnum[type] }).toUpperCase(),
    value: type,
  }));

  const sequenceHistoryTypeOptions = Object.values(SequenceTypeEnum).map((sequenceType) => ({
    label: formatMessage({ id: SequenceTypeDescriptionEnum[sequenceType] }).toUpperCase(),
    value: sequenceType,
  }));

  const sequenceTypeOptions = Object.values(SequenceTypeEnum).map((sequenceType) => ({
    label: formatMessage({ id: SequenceTypeDescriptionEnum[sequenceType] }),
    value: sequenceType,
  }));

  const eventsReportInputStateOptions = Object.values(EventsReportInputStateEnum).map((inputState) => ({
    label: formatMessage({ id: EventsReportInputStateMessageEnum[inputState] }),
    value: inputState,
  }));

  return {
    modOptions,
    modeOptions,
    rcpOptions,
    eventOptions,
    categoryOptions,
    yesNoOptions,
    serverOptions,
    permissionOptions,
    userStatusOptions,
    cardTypeOptions,
    cardBlockedOptions,
    separatorOptions,
    endlineOptions,
    eventsExportFormatsOptions,
    groupTypeOptions,
    glcOptions,
    announcementModOptions,
    scheduleModesOptions,
    scheduleTransitionOptions,
    scheduleHistoryTypeFilterOptions,
    sequenceHistoryTypeOptions,
    sequenceTypeOptions,
    eventsReportInputStateOptions,
  };
};
